import { z } from "zod";
import { User } from "@/core/models/global/User";

export type ResponseKind = "register" | "login";

/** Base class shared by register/login responses. */
export abstract class UserResponse {
    constructor(
        public readonly kind: ResponseKind,
        public user?: User,
        public token?: string
    ) { }
}

/** Helper: accept camelCase or PascalCase keys from the server. */
function liftCamelToPascal<T extends Record<string, unknown>>(o: unknown): T {
    if (!o || typeof o !== "object") return {} as T;
    const src = o as Record<string, unknown>;
    const out: Record<string, unknown> = { ...src };

    // copy camelCase -> PascalCase when PascalCase missing
    for (const k of Object.keys(src)) {
        if (/^[a-z]/.test(k)) {
            const pascal = k[0].toUpperCase() + k.slice(1);
            if (out[pascal] === undefined) out[pascal] = src[k];
        }
    }

    // Normalize common token/user variants
    out.Token ??= out.token ?? out.AccessToken ?? out.accessToken;
    out.User ??= out.user;

    // Normalize kind if present
    if (typeof out.kind === "string" && out.kind.length) {
        out.kind = String(out.kind).toLowerCase();
    } else if (typeof out.Kind === "string") {
        out.kind = String(out.Kind).toLowerCase();
    }

    return out as T;
}

/** Common, preprocessed base schema (strict on the inner object). */
const BaseObject = z.preprocess(
    liftCamelToPascal,
    z
        .object({
            kind: z.union([z.literal("register"), z.literal("login")]).optional(),
            Token: z.string().min(1).optional(),
            User: z.unknown().optional(), // hydrated later with User.fromJSON
            // add RefreshToken, ExpiresIn, etc., here if needed
        })
        .strict()
);

// eslint-disable-next-line @typescript-eslint/naming-convention
export const __UserResponseInternals = {
    BaseObject,
    liftCamelToPascal,
    toUserInstance: (u: unknown): User | undefined => (u == null ? undefined : User.fromJSON(u)),
};
